// In-buffer directive scanner. Recognizes dialect-specific client directives
// that modify parser behavior mid-document (QUOTED_IDENTIFIER flips, DELIMITER
// switches, search_path changes, etc.). Returns a list of directive
// occurrences in source order. PRD § 2.5 tier 1.

import type { DirectiveKind, DirectiveOccurrence, ParseOptions, SqlEngine } from './types';

// Line-by-line scan: directives are assumed single-line. Only a line-LEADING
// line comment (`--`, or `-- `/ `#` for MySQL) is stripped before matching —
// mid-line `--`/`#` and block comments (`/* */`, including multi-line spans)
// are NOT tracked. Consequences: (a) a line inside a multi-line `/* … */`
// block comment is scanned as if it were live SQL; (b) a directive textually
// following a mid-line `/*` on the same line can false-positive; (c) in
// practice the per-directive regexes are strict full-line-anchored (`^…;?\s*$`),
// so a commented-out or mid-comment directive rarely matches — the false-positive
// window is narrow but real.
const LINE_COMMENT_PG_SQL = /^\s*--.*$/;
const LINE_COMMENT_MYSQL = /^\s*(?:--\s|#).*$/;
const ON_OFF = /^(ON|OFF|TRUE|FALSE|1|0)$/i;

interface LineSlot {
  text: string;        // original line (without newline)
  startOffset: number; // offset of the line in documentText
}

function splitLines(text: string): LineSlot[] {
  const out: LineSlot[] = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) === 10 /* \n */) {
      out.push({ text: text.slice(start, i), startOffset: start });
      start = i + 1;
    }
  }
  if (start <= text.length) out.push({ text: text.slice(start), startOffset: start });
  return out;
}

function occurrence(
  kind: DirectiveKind,
  line: LineSlot,
  matched: string,
  effect: Partial<ParseOptions>,
): DirectiveOccurrence {
  const idx = line.text.indexOf(matched);
  const startOffset = line.startOffset + (idx >= 0 ? idx : 0);
  const endOffset = startOffset + matched.length;
  return {
    kind,
    range: { startOffset, endOffset },
    applyAt: endOffset,
    effect,
  };
}

function scanMssql(line: LineSlot): DirectiveOccurrence | null {
  const stripped = line.text.replace(/^\s*--.*$/, '').trim();
  if (!stripped) return null;
  const m = stripped.match(/^SET\s+QUOTED_IDENTIFIER\s+(ON|OFF)\s*;?\s*$/i);
  if (!m) return null;
  const value = m[1].toUpperCase() === 'ON';
  return occurrence('mssql-quoted-identifier', line, m[0], { quotedIdentifier: value });
}

function scanMysql(line: LineSlot): DirectiveOccurrence | null {
  const stripped = line.text.replace(LINE_COMMENT_MYSQL, '').trim();
  if (!stripped) return null;
  // `DELIMITER //` — note: non-terminated, line-scoped. MySQL's delimiter
  // directive does NOT end with the current delimiter.
  const m = stripped.match(/^DELIMITER\s+(\S+)\s*$/i);
  if (!m) return null;
  return occurrence('mysql-delimiter', line, m[0], { currentDelimiter: m[1] });
}

function parseSearchPath(raw: string): string[] {
  // Accepts: `SET search_path = a, b, c;` or `SET search_path TO a, b, c;`.
  // Each entry may be quoted with double-quotes ("foo") or left bareword.
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((entry) => {
      const q = entry.match(/^"(.*)"$/);
      return q ? q[1].replace(/""/g, '"') : entry;
    });
}

function scanPg(line: LineSlot): DirectiveOccurrence | null {
  const stripped = line.text.replace(LINE_COMMENT_PG_SQL, '').trim();
  if (!stripped) return null;

  const searchPath = stripped.match(
    /^SET(?:\s+(?:SESSION|LOCAL))?\s+search_path\s*(?:=|TO)\s*([^;]+?)\s*;?\s*$/i,
  );
  if (searchPath) {
    return occurrence('pg-search-path', line, searchPath[0], {
      searchPath: parseSearchPath(searchPath[1]),
    });
  }

  const scs = stripped.match(
    /^SET(?:\s+(?:SESSION|LOCAL))?\s+standard_conforming_strings\s*(?:=|TO)\s*(\S+?)\s*;?\s*$/i,
  );
  if (scs && ON_OFF.test(scs[1])) {
    const v = scs[1].toUpperCase();
    return occurrence('pg-standard-conforming-strings', line, scs[0], {
      standardConformingStrings: v === 'ON' || v === 'TRUE' || v === '1',
    });
  }

  return null;
}

function scanOracle(line: LineSlot): DirectiveOccurrence | null {
  const stripped = line.text.replace(/^\s*--.*$/, '').trim();
  if (!stripped) return null;
  const m = stripped.match(/^ALTER\s+SESSION\s+SET\s+CURRENT_SCHEMA\s*=\s*(\S+?)\s*;?\s*$/i);
  if (!m) return null;
  // Oracle doesn't have a ParseOptions field for current schema; record the
  // occurrence as a marker. Downstream slices (identifier folding,
  // DocumentStructure) consume the directive list; the resolver doesn't
  // mutate ParseOptions for it.
  return occurrence('oracle-current-schema', line, m[0], {});
}

function scanSqlite(line: LineSlot): DirectiveOccurrence | null {
  const stripped = line.text.replace(LINE_COMMENT_PG_SQL, '').trim();
  if (!stripped) return null;

  const attach = stripped.match(/^ATTACH(?:\s+DATABASE)?\s+.+?\s+AS\s+\S+\s*;?\s*$/i);
  if (attach) return occurrence('sqlite-attach', line, attach[0], {});

  const pragma = stripped.match(/^PRAGMA\s+.+?\s*;?\s*$/i);
  if (pragma) return occurrence('sqlite-pragma', line, pragma[0], {});

  return null;
}

type Scanner = (line: LineSlot) => DirectiveOccurrence | null;
const SCANNERS: Record<SqlEngine, Scanner> = {
  mssql: scanMssql,
  mysql: scanMysql,
  postgres: scanPg,
  oracle: scanOracle,
  sqlite: scanSqlite,
};

export function scanInBufferDirectives(
  engine: SqlEngine,
  documentText: string,
): DirectiveOccurrence[] {
  const scan = SCANNERS[engine];
  if (!scan) return [];
  const out: DirectiveOccurrence[] = [];
  for (const line of splitLines(documentText)) {
    const hit = scan(line);
    if (hit) out.push(hit);
  }
  return out;
}
